// ADR-096 Phase 3 — citation cohort discriminator + defensive normalizer.
//
// ╔══════════════════════════════════════════════════════════════════════╗
// ║ STRUCTURAL INVARIANT (DO NOT REGRESS DURING PHASE 6 LEGACY DELETION) ║
// ╠══════════════════════════════════════════════════════════════════════╣
// ║ Phase 2 backend (commit e5cc95c1) emits index=marker_index MIRROR on ║
// ║ ADR-096 cites so LEGACY render paths display ADR-096 chips correctly ║
// ║ today. Phase 3 is structurally a BEHAVIOR-PRESERVING REFACTOR — its  ║
// ║ value is enabling Phase 6 deletion of legacy ADR-082 paths after the ║
// ║ Phase 4 empirical gate clears. The discriminator + cohort classifier ║
// ║ are the load-bearing primitives that route per-cite render decisions ║
// ║ across MessageRenderer + InlineFootnotes + JobDetail-                ║
// ║ Drawer + chatStore.loadJobFromHistory without coupling them.         ║
// ║                                                                      ║
// ║ See ADR-096 §3.7 (backward-compat shim) + §10 D1-D5 SIGNED-OFF +     ║
// ║ §11 Phase 3 chat-ui consumer rewrite design.                         ║
// ╚══════════════════════════════════════════════════════════════════════╝

import type { PersistedCitationRef } from "@/types";

/**
 * Per-message citation cohort classification. The chat-ui must dispatch
 * render strategies based on which cohort the message's citations belong
 * to:
 *   - "adr096": ALL cites have marker_index → byMarkerIndex
 *     1:1 lookup (no consecutive-cluster collapse, no Jaccard
 *     claim_unsupported gate, supported-only Sources strip)
 *   - "legacy": ALL cites are ADR-082 shape → existing byIndex Map<int,
 *     CitationRef[]> with iter 11 image-wins + iter 14 F1 sibling
 *     preservation + iter 15 block-scoped canonical reset + iter 16
 *     filter-before-dedupe in the (since-deleted) Sources card + iter 18 claim_unsupported
 *     null-render in CitationChip
 *   - "mixed": defensive-only state — per-cite shape mixing within one
 *     m.citations is structurally impossible (single synth call emits one
 *     cohort) but cross-message mixing IS real (sidebar replay of
 *     legacy thread + fresh ADR-096 query in same session). Per-message
 *     classification means MessageRenderer sees only "adr096" OR
 *     "legacy" per its own render call; "mixed" surfaces ONLY if
 *     defensive narrowing accepts both shapes within one citation list,
 *     which we route via per-cite isADR096Citation discriminator.
 */
export type CitationCohort = "attribution" | "adr096" | "legacy" | "mixed";

/**
 * Per-cite discriminator. Returns true if + only if marker_index is a
 * number.
 *
 * marker_index is the structural ADR-097 numeric-index signal: the
 * backend stamps it (= the cited source number N) only on cites bound via
 * the numeric-index resolver. Legacy ADR-082 / Jaccard emitters never set
 * it. cited_text was retired with ADR-097 (provenance now comes from the
 * chunk `preview`, not a model-quoted span), so the earlier BOTH-gate
 * (marker_index AND cited_text) would misclassify every numeric-index cite
 * as legacy.
 */
export function isADR096Citation(cite: PersistedCitationRef): boolean {
  return typeof cite.marker_index === "number";
}

// ADR-098 §3.4: P-Cite cites carry answer_span_start (a char offset ≥ 0);
// Jaccard / numeric / ADR-096 paths emit null/undefined — typeof guards 0.
export function isAttributionCitation(cite: PersistedCitationRef): boolean {
  return typeof cite.answer_span_start === "number";
}

/**
 * Per-message cohort classifier for routing in MessageRenderer.useMemo.
 * (It also gated the Sources card's top-level branch until that card was
 * deleted 2026-08-09.) Used at the boundary between
 * chatStore.applyEvent (dispatcher unchanged across cohorts) and the
 * render components (cohort-aware).
 *
 * Empty array → "legacy" (no-op path; mirrors the pre-ADR-096 behavior of the
 * since-deleted Sources card, which returned null on zero unique chunks).
 */
export function classifyCitationCohort(
  citations: ReadonlyArray<PersistedCitationRef>,
): CitationCohort {
  if (citations.length === 0) {
    return "legacy";
  }
  let adr096Count = 0;
  let attributionCount = 0;
  let legacyCount = 0;
  for (const cite of citations) {
    if (isAttributionCitation(cite)) {
      attributionCount += 1;
    } else if (isADR096Citation(cite)) {
      adr096Count += 1;
    } else {
      legacyCount += 1;
    }
  }
  if (attributionCount > 0 && adr096Count === 0 && legacyCount === 0) {
    return "attribution";
  }
  if (adr096Count > 0 && attributionCount === 0 && legacyCount === 0) {
    return "adr096";
  }
  if (legacyCount > 0 && adr096Count === 0 && attributionCount === 0) {
    return "legacy";
  }
  return "mixed";
}

/**
 * Defensive snapshot narrower accepting BOTH legacy ADR-082 shape AND
 * new ADR-096 shape. Extends iter 19 narrowCitations (chunk_id required
 * as minimum guarantor for downstream render) with ADR-096 field
 * preservation.
 *
 * Required fields (per iter 19): chunk_id + doc_id. The index field is
 * conditionally required:
 *   - Legacy shape: typeof index === "number" required
 *   - ADR-096 shape: typeof marker_index === "number" required (and
 *     Phase 2 backend mirrors index=marker_index so index is also
 *     present, but we don't assume it during narrowing)
 *
 * Used at the chatStore.loadJobFromHistory boundary (snapshot replay
 * for ADR-091 Phase 5b job-history) + JobDetailDrawer drawer hydration.
 * The cohort classifier downstream routes the narrowed array to the
 * appropriate render path.
 *
 * Non-array input → []. Malformed entries silently dropped.
 */
export function normalizeCitations(raw: unknown): PersistedCitationRef[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: PersistedCitationRef[] = [];
  for (const item of raw) {
    if (item === null || typeof item !== "object") {
      continue;
    }
    const r = item as Record<string, unknown>;
    if (typeof r.chunk_id !== "string" || typeof r.doc_id !== "string") {
      continue;
    }
    // ADR-097 numeric-index shape: marker_index present → accept
    // Legacy shape: index field present → accept via legacy path
    // Either path needs at least one numeric marker reference
    const hasLegacyIndex = typeof r.index === "number";
    const hasMarkerIndex = typeof r.marker_index === "number";
    if (!hasLegacyIndex && !hasMarkerIndex) {
      continue;
    }
    out.push(item as PersistedCitationRef);
  }
  return out;
}

// ADR-099: stable per-UNIQUE-SOURCE numbering (1..K by FIRST APPEARANCE / arrival order)
// for the P-Cite attribution cohort, used IDENTICALLY by the inline pill + Sources strip +
// detail panel so the same source reuses one number everywhere. Streaming P-Cite emits
// citations progressively (chatStore `case "citation"` pure-appends), so the retired
// index-sort reshuffled numbers as later cites arrived; iterating in arrival order keeps
// each chunk_id's number stable + monotonic as the array grows by append (a known chunk
// keeps its number; a new chunk_id gets the next higher). EMPTY for non-attribution
// cohorts (legacy [N]/numeric keep their literal-marker numbers — marker is in the text).
export function buildSourceNumberByChunkId(citations: PersistedCitationRef[]): Map<string, number> {
  const map = new Map<string, number>();
  if (classifyCitationCohort(citations) !== "attribution") return map;
  let n = 0;
  for (const c of citations) {
    if (!map.has(c.chunk_id)) {
      n += 1;
      map.set(c.chunk_id, n);
    }
  }
  return map;
}
